import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './3SCLandingPage.module.css';
import FeatureHeader from '../components/FeatureHeader';
import { imageConstants, timelineSteps, whySectionData, faqData, benefitsData } from '../constants';


// Icon components
const CheckIcon = () => (
  <svg 
    className={styles.benefitIcon}
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5"></path>
  </svg>
);

const ChevronDownIcon = ({ expanded }) => (
  <svg 
    className={`${styles.faqIcon} ${expanded ? styles.expanded : ''}`}
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const SCLandingPage = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const handleApplyClick = () => {
    navigate('/sustainability/terms');  
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className={styles.landingPage}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <FeatureHeader
          src="/images/feature-headers/3sc-certification.png"
          alt="Suspire Sustainability Score Certificate"
          align="center"
          style={{ marginBottom: '1.25rem' }}
        />
        <h2 className={styles.subline}>One Badge. Three Abilities. Endless Compounding Trust.</h2>
        <p className={styles.descriptor}>
          A badge, aligned to the UN SDGs and verified by Suspire’s rigorous framework, so shoppers, retailers, and investors trust your eco‑claims in a single glance.
        </p>
        
        {/* Primary CTA */}
        <div className={styles.ctaSection}>
          <button className={styles.primaryButton} onClick={handleApplyClick}>
            Start My 3S Certification
          </button>
          <p className={styles.secondaryText}>
            Complete your brands initial assessment in less than 20 minutes.
          </p>
        </div>
      </div>

      {/* Visual Cards Section */}
      <div className={styles.visualSection}>
        <div className={styles.visualCards}>
          <div className={styles.visualCard}>
            <div className={styles.mockupImage}>
              <img src={imageConstants.banner1} alt="Official Certificate" />
            </div>
            <h3>Official Certificate</h3>
            <p>Download and display your official 3S certificate across platforms</p>
          </div>
          <div className={styles.visualCard}>
            <div className={styles.mockupImage}>
              <img src={imageConstants.banner2} alt="Official Badges" />
            </div>
            <h3>Official Badges</h3>
            <p>A gold badge for excellence and a trust badge for meeting sustainability standards</p>
          </div>
          <div className={styles.visualCard}>
            <div className={styles.mockupImage}>
              <img src={imageConstants.banner3} alt="Brand Showcase" />
            </div>
            <h3>Brands Using 3SC</h3>
            <p>Join hundreds of brands proudly displaying their sustainability score</p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className={styles.howItWorksSection}>
        <h2 className={styles.sectionTitle}>How It Works</h2>
        <div className={styles.timeline}>
          {timelineSteps.map((step, index) => (
            <div key={index} className={styles.timelineItem}>
              <div className={styles.timelineNumber}>{step.number}</div>
              <div className={styles.timelineContent}>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
        <p className={styles.completionNote}>Avg. completion time: 2–3 business days</p>
      </div>

      {/* Why Section */}
      <div className={styles.whySection}>
        <h2 className={styles.sectionTitle}>The “Why” in Three Abilities</h2>
        <div className={styles.whyGrid}>
          {whySectionData.map((why, index) => (
            <div className={styles.whyColumn}>
            <h3 className={styles.whyTitle}>{why.title}</h3>
            <p className={styles.whyDescription}>{why.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className={styles.benefitsSection}>
        <h2 className={styles.sectionTitle}>Benefit for All</h2>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitColumn}>
            <h3>For Your Brand</h3>
            <ul className={styles.benefitList}>
              {benefitsData.brand.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className={styles.benefitColumn}>
            <h3>For Customers</h3>
            <ul className={styles.benefitList}>
              {benefitsData.customers.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className={styles.benefitColumn}>
            <h3>For suspire Marketplace</h3>
            <ul className={styles.benefitList}>
              {benefitsData.marketplace.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className={styles.faqSection}>
        <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
        <div className={styles.faqList}>
          {faqData.map((faq, index) => (
            <div key={index} className={styles.faqItem}>
              <button 
                className={styles.faqQuestion}
                onClick={() => toggleFAQ(index)}
              >
                {faq.question}
                <ChevronDownIcon expanded={expandedFAQ === index} />
              </button>
              {expandedFAQ === index && (
                <div className={styles.faqAnswer}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SCLandingPage;